import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cpu, BookOpenCheck, ArrowRight, MousePointerClick, Eye, Zap } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { DEMOS_COPY } from "@/data/demosCopy";

const SITE = "https://oceandata.ca";
const STEP_ICONS = [MousePointerClick, Eye, Zap];
const DEMO_ICONS = { "hub-card-revenue": Cpu, "hub-card-books": BookOpenCheck };

export default function DemosHubPage({ lang = "en" }) {
  const t = DEMOS_COPY[lang] || DEMOS_COPY.en;

  useEffect(() => {
    document.title = t.docTitle;
    document.documentElement.lang = lang;
    const links = [];
    Object.entries(t.hreflangs).forEach(([hreflang, path]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = `${SITE}${path}`;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    });
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, [t, lang]);

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang={lang}>
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            {t.eyebrow}
          </p>
          <h1
            data-testid="demos-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="demos-headline-line-1">{t.h1a}</MaskedLine>
            <MaskedLine delay={0.3} testId="demos-headline-line-2">
              <span className="text-brand-teal">{t.h1b}</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.heroSub}
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-14 mb-16">
            {t.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200 p-6">
                    <span className="w-10 h-10 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center shrink-0">
                      <Icon size={17} />
                    </span>
                    <div>
                      <p className="font-display font-semibold text-brand-navy text-base mb-1">{s.title}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {t.demos.map((d, i) => {
              const Icon = DEMO_ICONS[d.id];
              return (
                <Reveal key={d.id} delay={i * 0.1}>
                  <Link
                    to={d.to}
                    data-testid={d.id}
                    className="group block h-full rounded-3xl bg-brand-navy text-white p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(14,20,51,0.5)]"
                  >
                    <span className="w-12 h-12 rounded-2xl bg-brand-teal/15 text-brand-teal flex items-center justify-center mb-7">
                      <Icon size={22} />
                    </span>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-gold font-semibold mb-3">
                      {d.name}
                    </p>
                    <h2 className="font-display font-semibold text-2xl sm:text-3xl leading-snug mb-4">
                      “{d.hook}”
                    </h2>
                    <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-7">
                      {d.body}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-8">
                      {d.tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-white/10 text-slate-200 text-xs font-semibold px-3.5 py-1.5">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-gold group-hover:text-white transition-colors duration-300">
                      {t.tryDemo}
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-12 text-sm text-slate-500 max-w-2xl leading-relaxed">
              {t.notePre}{" "}
              <Link to="/#flow" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                {t.noteFlow}
              </Link>
              {t.noteMid}{" "}
              <Link to={t.noteApproachTo} className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                {t.noteApproach}
              </Link>
              {" "}{t.notePost}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
